import React, { useState } from "react";
import {
  Button,
  Checkbox,
  DropdownField,
  FormSection,
  MultiSelectField,
  TextField,
  fieldLabelClasses,
  useProblemReport,
  useToast,
  type ProblemReport,
} from "../../../components/ui";
import {
  normalisePhoneInput,
  normalisePincodeInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import { STATE_OPTIONS, citiesOfState } from "../../../lib/locations";
import ExperienceField from "./ExperienceField";
import ProfilePhotoCard from "./ProfilePhotoCard";
import { GENDER_OPTIONS, SPECIALIZATION_OPTIONS } from "../profileCodes";
import { useSpecializationOptions } from "../hooks/useSpecializationOptions";
import {
  MAX_EXPERIENCE_YEARS,
  ProfileErrors,
  ValidatedField,
  normaliseAgeInput,
} from "../validation";
import type { DietitianProfile } from "../types/settings.types";

/**
 * The validated fields in the order they are laid out, which is the order the
 * first problem has to be looked for in — `VALIDATED_FIELDS` is grouped by
 * required-ness instead, and would send you to the wrong one.
 */
const FIELD_ORDER: ValidatedField[] = [
  "firstName",
  "lastName",
  "gender",
  "age",
  "pincode",
  "phone",
  "whatsapp",
  "experienceYears",
  "experienceMonths",
];

/** Stable id per control, so a failing field can be reached and focused. */
const controlId = (field: ValidatedField) => `profile-${field}`;

interface ProfileFormProps {
  profile: DietitianProfile;
  /** Patch merge — every control sends only the key it owns. */
  onChange: (patch: Partial<DietitianProfile>) => void;
  /** Failing fields, keyed by field name. */
  errors: ProfileErrors;
  onSave: () => void;
  /** Puts the saved answers back and closes the form. */
  onCancel: () => void;
  /** Save stays disabled until something differs from the saved copy. */
  dirty: boolean;
  /** True while the save is in flight — both buttons stand down. */
  saving?: boolean;
  /**
   * Whether the profile as stored is already complete, which is what the Save
   * button is named after: finishing the setup the first time, changing the
   * answers every time after. Read from the saved record rather than the boxes
   * on screen — see the page.
   */
  setUp?: boolean;
  /**
   * Filled with this form's own way of pointing at what is missing, so the
   * page above can ask for it — the rail's unsaved prompt does. See
   * `useProblemReport`.
   */
  problems?: ProblemReport;
}

/**
 * The General tab with its answers open for editing: who the dietitian is, how
 * to reach them, and what they practise — plus the picture beside it all.
 *
 * The same shape as the brand form, and built from the same controls, because
 * it is the same job: a long form the panel's other long form has already
 * settled the look of. What differs is what it saves to — see
 * `settingsService`.
 */
const ProfileForm: React.FC<ProfileFormProps> = ({
  profile,
  onChange,
  errors,
  onSave,
  onCancel,
  dirty,
  saving = false,
  setUp = false,
  problems,
}) => {
  const toast = useToast();

  // While the read is out, or if it fails, the field falls back to the static
  // list rather than sitting empty — see `useSpecializationOptions`.
  const { options: fetchedSpecializations, status: specializationsStatus } =
    useSpecializationOptions();
  const specializationOptions =
    specializationsStatus === "ready"
      ? fetchedSpecializations
      : SPECIALIZATION_OPTIONS;

  // A field's error only surfaces once it has been visited, so the form does
  // not open covered in red before anything has been typed.
  const [touched, setTouched] = useState<
    Partial<Record<ValidatedField, boolean>>
  >({});

  const visit = (field: ValidatedField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  const errorFor = (field: ValidatedField) =>
    touched[field] ? errors[field] : undefined;

  const valid = Object.keys(errors).length === 0;

  // While the numbers are linked, editing the phone has to carry the WhatsApp
  // number with it — otherwise the two drift apart behind a ticked box.
  const handlePhoneChange = (raw: string) => {
    const phone = normalisePhoneInput(raw);
    onChange(
      profile.whatsappSameAsPhone ? { phone, whatsapp: phone } : { phone }
    );
  };

  // The city list belongs to the state, so changing the state drops whatever
  // city was picked under the old one — keeping it would leave the address
  // naming a city that is not in the state beneath it.
  const handleStateChange = (state: string) => onChange({ state, city: "" });

  // Unticking leaves the copied number in place rather than blanking it: it is
  // almost always the right starting point for the edit that follows.
  const handleSameAsPhone = (checked: boolean) =>
    onChange(
      checked
        ? { whatsappSameAsPhone: true, whatsapp: profile.phone }
        : { whatsappSameAsPhone: false }
    );

  const handleCancel = () => {
    setTouched({});
    onCancel();
  };

  /**
   * Points at the problem rather than describing it: every failing field
   * reports itself at once, and the page moves to the first of them and puts
   * the cursor in it.
   */
  const reportProblems = () => {
    const firstBad = FIELD_ORDER.find((field) => errors[field]);
    if (!firstBad) return;

    setTouched(Object.fromEntries(FIELD_ORDER.map((field) => [field, true])));

    // Focus without its own jump, so the smooth scroll is the only movement
    // and the field lands in the middle of the column rather than its edge.
    const control = document.getElementById(controlId(firstBad));
    control?.focus({ preventScroll: true });
    control?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  // Also reachable from the page above, for the one press that is not made on
  // this form: the rail's unsaved prompt offering to save it.
  useProblemReport(problems, reportProblems);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A save already in flight: pressing Enter in a field must not send a
    // second one after it.
    if (saving) return;

    // Enter in a field reaches this even while the button is off, so the
    // incomplete form is marked up here too rather than silently ignored.
    if (!valid) {
      reportProblems();
      return;
    }

    onSave();
  };

  return (
    <form
      className="flex flex-col lg:h-full lg:min-h-0"
      onSubmit={handleSubmit}
    >
      {/* Only the sections scroll — the actions below stay put, so Save is
          reachable from anywhere in a form this long. `overflow` is set at
          every width, but it only bites from `lg`, where the column is height
          bound; below that the box grows and the page scrolls as before. */}
      <div className="lg:min-h-0 lg:flex-1 lg:pr-1 pb-5 custom-scrollbar-overlay">
        {/* The photo column is sized by its own contents rather than a fraction
            of the row, so however wide the page gets there is no strip of empty
            column between it and the form. Below `lg` it drops under the
            sections, where a 136px disc is not worth a column of its own.
            `items-start` keeps it that height too: left to stretch, the card
            would run the full length of the form beside it, and the white would
            read as the page's background rather than as a card. */}
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px] 3xl:grid-cols-[minmax(0,1fr)_340px] 3xl:gap-6">
          <div className="flex flex-col gap-5">
            <FormSection title="Basic details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <TextField
                  label="First name"
                  id={controlId("firstName")}
                  placeholder="eg. Riya"
                  autoComplete="given-name"
                  required
                  value={profile.firstName}
                  error={errorFor("firstName")}
                  onBlur={visit("firstName")}
                  // Cased on the way in rather than on save, so the heading the
                  // overview draws is exactly what the field holds.
                  onValueChange={(firstName) =>
                    onChange({ firstName: toNameCase(firstName) })
                  }
                />
                <TextField
                  label="Last name"
                  id={controlId("lastName")}
                  placeholder="eg. Roy"
                  autoComplete="family-name"
                  required
                  value={profile.lastName}
                  error={errorFor("lastName")}
                  onBlur={visit("lastName")}
                  onValueChange={(lastName) =>
                    onChange({ lastName: toNameCase(lastName) })
                  }
                />
                <DropdownField
                  label="Gender"
                  id={controlId("gender")}
                  placeholder="eg. Female"
                  options={GENDER_OPTIONS}
                  required
                  value={profile.gender}
                  error={errorFor("gender")}
                  onBlur={visit("gender")}
                  onValueChange={(gender) => onChange({ gender })}
                />
                <TextField
                  label="Age"
                  id={controlId("age")}
                  placeholder="eg. 25"
                  inputMode="numeric"
                  maxLength={3}
                  value={profile.age}
                  error={errorFor("age")}
                  onBlur={visit("age")}
                  onValueChange={(age) =>
                    onChange({ age: normaliseAgeInput(age) })
                  }
                />
              </div>
            </FormSection>

            <FormSection title="Communication details">
              <div className="flex flex-col gap-4">
                <TextField
                  label="Full address (optional)"
                  placeholder="1202, Galaxy apartment, SV Road"
                  value={profile.address}
                  onValueChange={(address) => onChange({ address })}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                  {/* State leads City, because it is what fills it: laid out the
                    other way round the reader meets a dead control before the
                    one that brings it to life. Both lists are typed into
                    rather than scrolled — 34 states and up to 120 cities are
                    more than anyone wants to hunt through. */}
                  <DropdownField
                    label="State"
                    placeholder="eg. Maharashtra"
                    options={STATE_OPTIONS}
                    searchable
                    value={profile.state}
                    onValueChange={handleStateChange}
                  />
                  <DropdownField
                    label="City"
                    placeholder={
                      profile.state ? "eg. Mumbai" : "Select a state first"
                    }
                    options={citiesOfState(profile.state)}
                    disabled={!profile.state}
                    // Reaching for the city before there is a state to fill it is
                    // a reasonable thing to do, so it gets an answer rather than
                    // a control that simply refuses to respond.
                    onDisabledClick={() =>
                      toast.warning(
                        "Select a state first",
                        "The city list is filled by the state you pick.",
                      )
                    }
                    searchable
                    // The list covers the common cases and cannot claim to be
                    // exhaustive: a town it has never heard of is still where
                    // somebody practises. Typing one offers it as a "Use …" row
                    // under whatever matched, and a value already saved that
                    // way shows in the closed control rather than reading as
                    // blank.
                    creatable
                    value={profile.city}
                    onValueChange={(city) => onChange({ city })}
                  />
                  <TextField
                    label="Pincode"
                    id={controlId("pincode")}
                    placeholder="400130"
                    inputMode="numeric"
                    maxLength={6}
                    value={profile.pincode}
                    error={errorFor("pincode")}
                    onBlur={visit("pincode")}
                    onValueChange={(pincode) =>
                      onChange({ pincode: normalisePincodeInput(pincode) })
                    }
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  <TextField
                    label="Phone number"
                    id={controlId("phone")}
                    placeholder=" 7869 369 854"
                    // The field holds the ten subscriber digits; the dialling
                    // code is drawn beside them and put back on for the API.
                    prefix="+91"
                    inputMode="numeric"
                    autoComplete="tel"
                    // The normaliser already stops at ten digits; `maxLength` is
                    // what makes the browser say so before the keystroke lands.
                    maxLength={10}
                    required
                    value={profile.phone}
                    error={errorFor("phone")}
                    onBlur={visit("phone")}
                    onValueChange={handlePhoneChange}
                  />
                  <div>
                    <TextField
                      label="WhatsApp number"
                      id={controlId("whatsapp")}
                      placeholder=" 7869 369 854"
                      prefix="+91"
                      inputMode="numeric"
                      maxLength={10}
                      // Mirrored fields are read-only: the phone beside it is the
                      // one place the number is edited while the box is ticked.
                      disabled={profile.whatsappSameAsPhone}
                      value={profile.whatsapp}
                      // Optional, so it only reports itself once it holds a part
                      // number — never for being left empty.
                      error={errorFor("whatsapp")}
                      onBlur={visit("whatsapp")}
                      onValueChange={(whatsapp) =>
                        onChange({ whatsapp: normalisePhoneInput(whatsapp) })
                      }
                    />
                    <Checkbox
                      label="Same as phone number"
                      className="mt-2"
                      checked={profile.whatsappSameAsPhone}
                      onCheckedChange={handleSameAsPhone}
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection title="Professional details">
              <div className="flex flex-col gap-4">
                <div>
                  <span className={`${fieldLabelClasses} mb-1.5`}>
                    Work experience
                  </span>
                  {/* Two figures under one caption, because that is how a length
                    of service is said: eight years and four months, not 8.33
                    years. Each is its own control, and each says which half it
                    is to a screen reader — see `ExperienceField`. */}
                  <div className="grid grid-cols-2 gap-4 sm:gap-5 sm:max-w-[420px]">
                    <ExperienceField
                      unit="years"
                      id={controlId("experienceYears")}
                      value={profile.experienceYears}
                      error={errorFor("experienceYears")}
                      onBlur={visit("experienceYears")}
                      max={MAX_EXPERIENCE_YEARS}
                      onValueChange={(experienceYears) =>
                        onChange({ experienceYears })
                      }
                    />
                    <ExperienceField
                      unit="months"
                      id={controlId("experienceMonths")}
                      value={profile.experienceMonths}
                      error={errorFor("experienceMonths")}
                      onBlur={visit("experienceMonths")}
                      // Twelve months is a year, and the field beside this one is
                      // where a year goes.
                      max={11}
                      onValueChange={(experienceMonths) =>
                        onChange({ experienceMonths })
                      }
                    />
                  </div>
                </div>

                <MultiSelectField
                  label="Specializations"
                  values={profile.specializations}
                  placeholder="eg. Weight management"
                  options={specializationOptions}
                  onValuesChange={(specializations) =>
                    onChange({ specializations })
                  }
                />
              </div>
            </FormSection>
          </div>

          {/* The caption row the sections opposite carry, stood in for: a
              `FormSection` spends its first 40px on the `h-8` heading and its
              `mb-2`, and without the same allowance here the photo card starts
              that much above the card beside it. With it, both columns' cards
              begin on the same line — the arrangement `FormSection` already
              describes for the brand screen.

              Only from `lg`: below that the photo drops under the sections,
              where there is nothing beside it to line up with. */}
          <div className="lg:pt-10">
            <ProfilePhotoCard
              photo={profile.photo}
              name={profile.firstName}
              onPhotoChange={(photo) => onChange({ photo })}
            />
          </div>
        </div>
      </div>

      {/* Pinned under the scrolling sections the way My brand's actions are, so
          Save is reachable from anywhere in a form this long rather than only
          from its end. Carded like the sections so it reads as part of the same
          form rather than page furniture. From `lg` up the sections above have
          taken all the height there is and this sits at the foot of it; below
          that the page scrolls as a whole and the bar is an ordinary last
          child, which on a phone is where it belongs anyway.

          Both fills are load-bearing: the page tint covers the gap under the
          rule, the white covers the card as the sections pass beneath it. */}
      <div className="sticky bottom-0 z-10 flex-shrink-0 border-t border-line bg-[#FCFCFD] pt-4">
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-white p-3">
          {/* Off until every required field is filled, and off the soft way
              while one is missing so a press still marks up what is — the same
              bargain as `BrandDetailsForm`, where it is spelled out. Before the
              first edit and mid-save it is off the plain way: there is nothing
              to explain about a form with nothing in it to save. */}
          <Button
            type={valid ? "submit" : "button"}
            size="md"
            disabled={!dirty || !valid || saving}
            onDisabledClick={
              dirty && !valid && !saving ? reportProblems : undefined
            }
            aria-invalid={dirty && !valid ? true : undefined}
            className="min-w-[130px]"
          >
            {/* A profile that is already filled in is being changed rather than
                set up, and the button says which — the first save is the one
                that names the job it finishes. */}
            {saving
              ? setUp
                ? "Saving changes…"
                : "Saving setup…"
              : setUp
              ? "Save changes"
              : "Save setup"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={handleCancel}
            // Cancelling mid-save would restore values the store is at that
            // moment replacing, so it waits for the answer like everything else.
            disabled={saving}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProfileForm;
